import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import { JsonType } from 'posthog-js';

/**
 * Applies the `?featureFlagsOn=`/`?featureFlagsOff=` query-parameter overrides to a
 * base flag value (comma-separated flag names). Shared by {@link useFeatureFlag} and
 * the Rust flag resolver (see WasmContext) so both the TS and WASM sides honor the
 * same overrides.
 *
 * @param flagName - The flag being resolved
 * @param value - The base value (e.g. from PostHog), returned when no override matches
 * @param search - A location search string (e.g. `window.location.search`)
 */
export function applyFeatureFlagOverride(
  flagName: string,
  value: boolean | undefined,
  search: string,
): boolean | undefined {
  const params = new URLSearchParams(search);
  const flagsOn = params.get('featureFlagsOn');
  if (flagsOn && flagsOn.split(',').some((flag) => flag.trim() === flagName)) {
    return true;
  }
  const flagsOff = params.get('featureFlagsOff');
  if (flagsOff && flagsOff.split(',').some((flag) => flag.trim() === flagName)) {
    return false;
  }
  return value;
}

/**
 * Hook that wraps useFeatureFlagEnabled and adds query parameter overrides.
 *
 * Query parameters:
 * - featureFlagsOn=flag1,flag2 - if the flag name exists in this list, always return true
 * - featureFlagsOff=flag1,flag2 - if the flag name exists in this list, always return false
 *
 * @param flagName - The name of the feature flag to check
 * @returns The feature flag value, potentially overridden by query parameters
 */
export const useFeatureFlag = (flagName: string): { value: boolean | undefined; variantData: JsonType | undefined } => {
  const [searchParams] = useSearchParams();
  const originalValue = useFeatureFlagEnabled(flagName);
  const variantData = useFeatureFlagPayload(flagName);
  return useMemo(
    () => ({
      value: applyFeatureFlagOverride(flagName, originalValue, searchParams.toString()),
      variantData,
    }),
    [flagName, searchParams, originalValue, variantData],
  );
};
